import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { NoPermissionPage, getUserRoles, showGlobalError } from "@shared";
import { useQuery } from "@tanstack/react-query";
import i18next from "i18next";

import { CurrentUserResponse, currentUserApi } from "@/api";
import { ResponseError } from "@/api/api-client";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { useWarmUserSettings } from "@/common/hooks/useUserSettings";
import { QueryKey } from "@/query/Query";

import CurrentUserContext from "./CurrentUserContext";
import { UserModel, UserPermission } from "./CurrentUserContextProvider.types";

function mapRolesToPermissions(roles: string[]): UserPermission[] {
  return Object.values(UserPermission).filter((p) => roles.includes(p));
}

const MAX_RETRIES = 3;

/**
 * The backend denies a staff login it cannot resolve to a Berater:in (no FSW-Portal ID, or the counselor
 * sync failed) with 403 — never 401, which would start a new sign-in that Keycloak's still-valid session
 * answers at once, in a loop.
 */
function isForbidden(error: unknown): boolean {
  return error instanceof ResponseError && error.response.status === 403;
}

const CurrentUserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();

  const { data: currentUserData, error } = useQuery<CurrentUserResponse | null | undefined>({
    queryKey: [QueryKey.CurrentUser],
    // Handled here rather than by the global error toast: a denied login gets a page, not a toast.
    queryFn: () => currentUserApi.getCurrentUser(withLocalErrorHandling),
    staleTime: Infinity,
    retry: (failureCount, queryError) => !isForbidden(queryError) && failureCount < MAX_RETRIES,
  });

  // Every other failure still surfaces the way the global error middleware would show it.
  useEffect(() => {
    if (error && !isForbidden(error)) {
      showGlobalError(i18next.t("common.serverError"));
    }
  }, [error]);

  useWarmUserSettings();

  const currentUser = useMemo<UserModel | undefined>(() => {
    if (!auth.user?.profile) {
      return undefined;
    }

    if (!currentUserData) {
      return undefined;
    }

    const profile = auth.user.profile;

    const permissions = mapRolesToPermissions(getUserRoles(auth.user));

    return {
      id: profile.sub,
      firstName: profile.given_name ?? "",
      lastName: profile.family_name ?? "",
      name: profile.name ?? "",
      userName: profile.preferred_username ?? "",
      dateOfBirth: new Date(),
      permissions,
      counselorId: currentUserData.counselorId ?? undefined,
      userCode: currentUserData.userCode,
    };
  }, [auth.user, currentUserData]);

  if (isForbidden(error)) {
    return <NoPermissionPage />;
  }

  if (!currentUser) {
    return null;
  }

  return <CurrentUserContext.Provider value={{ currentUser, hasPermission }}>{children}</CurrentUserContext.Provider>;

  function hasPermission(permission: UserPermission) {
    if (!currentUser) {
      return false;
    }

    if (currentUser.permissions.includes(permission)) {
      return true;
    }

    // Defense in depth: supervisor inherits staff (Keycloak composite should handle this,
    // but we check explicitly in case the composite role isn't expanded in the token)
    if (permission === UserPermission.SulStaff && currentUser.permissions.includes(UserPermission.Supervisor)) {
      return true;
    }

    return false;
  }
};

export default CurrentUserContextProvider;
